import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from '../features/authSlice';
import { useStateContext } from "../contexts/ContextProvider";

import StoriesComponent from "./components/StoriesComponent";
import InstagramComponent from "./components/InstagramComponent";

import {
  FaTrash,
  FaPlus,
  FaSearch,
  FaTimesCircle,
  FaLeaf,
  FaBookOpen,
  FaInstagram,
} from "react-icons/fa";

const KATEGORI_LIST = ["Semua", "Case Study", "News", "Article", "Post"];

const ListStories = () => {
  const [stories, setStories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeKategori, setActiveKategori] = useState("Semua");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentColor, currentMode } = useStateContext();

  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  const themeProps = { accentColor, isDark, cardBg, borderColor, textPrimary, textSecondary };

  useEffect(() => { dispatch(getMe()); }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) { getStories(); } else { navigate('/'); }
  }, [navigate, user]);

  const getStories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/stories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setStories([]);
    }
  };

  // ── Split data ───────────────────────────────────────────────────────────
  const contentStories = stories.filter((s) => s.kategori !== "Post");
  const instagramPosts = stories.filter((s) => s.kategori === "Post");

  const isPostView = activeKategori === "Post";

  const filteredContent = contentStories.filter((s) => {
    const matchKategori = activeKategori === "Semua" || s.kategori === activeKategori;
    const plain = (s.deskripsi || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
    const matchSearch =
      (s.judul || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      plain.toLowerCase().includes(searchTerm.toLowerCase());
    return matchKategori && matchSearch;
  });

  const filteredPosts = instagramPosts.filter((s) => {
    if (activeKategori !== "Semua" && activeKategori !== "Post") return false;
    if (!searchTerm) return true;
    return (s.urlInstagram || "").toLowerCase().includes(searchTerm.toLowerCase());
  });

  const showContent = activeKategori !== "Post";
  const showPosts = activeKategori === "Semua" || activeKategori === "Post";
  const totalShown = (showContent ? filteredContent.length : 0) + (showPosts ? filteredPosts.length : 0);

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteClick = (story) => { setSelectedStory(story); setShowDeleteModal(true); };
  const handleDeleteCancel = () => { setShowDeleteModal(false); setSelectedStory(null); };
  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.delete(`${apiUrl}/stories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteModal(false);
      setSelectedStory(null);
      getStories();
    } catch (error) {
      console.error('Error deleting stories:', error);
    }
    handleDeleteCancel();
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaBookOpen className="absolute bottom-40 left-20 text-7xl" style={{ color: accentColor }} />
          <FaLeaf className="absolute top-60 right-1/4 text-5xl" style={{ color: accentColor }} />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-8">
          <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                  <FaBookOpen className="text-white text-2xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                      Stories Management
                    </span>
                    <span className="text-xs" style={{ color: textSecondary }}>—</span>
                    <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                      Rumoh Pangan Aceh
                    </span>
                  </div>
                  <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>Data Stories</h1>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>Manajemen artikel dan berita Rumoh Pangan Aceh</p>
                </div>
              </div>
              <button
                onClick={() => navigate("/add-stories")}
                className="flex items-center justify-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 hover:shadow-lg font-medium"
                style={{ backgroundColor: accentColor }}
              >
                <FaPlus className="text-sm" />
                Tambah Stories
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── Search & Filter ── */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="mb-6">
          <div className="p-4 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-1 w-full">
                  <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-sm" style={{ color: textSecondary }} />
                  <input
                    type="text"
                    placeholder={isPostView ? "Cari URL Instagram..." : "Cari judul"}
                    className="w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 outline-none"
                    style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textPrimary }}
                    onFocus={(e) => (e.target.style.borderColor = accentColor)}
                    onBlur={(e) => (e.target.style.borderColor = inputBorder)}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Kategori pills */}
              <div className="flex flex-wrap gap-2">
                {KATEGORI_LIST.map((kat) => (
                  <button
                    key={kat}
                    onClick={() => setActiveKategori(kat)}
                    className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200"
                    style={
                      activeKategori === kat
                        ? { backgroundColor: kat === "Post" ? "#E1306C" : accentColor, color: "#fff" }
                        : { backgroundColor: `${accentColor}12`, color: textSecondary, border: `1px solid ${borderColor}` }
                    }
                  >
                    {kat === "Post" && <FaInstagram className="text-[10px]" />}
                    {kat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Content ── */}
        {totalShown === 0 ? (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="py-24 text-center rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <FaBookOpen className="text-6xl mb-4 mx-auto opacity-30" style={{ color: textSecondary }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>Tidak ada stories ditemukan</h3>
            <p className="text-sm mb-6" style={{ color: textSecondary }}>
              {searchTerm || activeKategori !== "Semua" ? "Coba ubah kata kunci atau kategori" : "Tambahkan stories baru untuk memulai"}
            </p>
            {!searchTerm && activeKategori === "Semua" && (
              <button
                onClick={() => navigate("/add-stories")}
                className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl transition-all duration-300 font-medium"
                style={{ backgroundColor: accentColor }}
              >
                <FaPlus className="text-sm" />
                Tambah Stories
              </button>
            )}
          </motion.div>
        ) : (
          <div className="space-y-8">
            {/* Artikel / News / Case Study */}
            {showContent && filteredContent.length > 0 && (
              <div>
                {activeKategori === "Semua" && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1" style={{ backgroundColor: borderColor }} />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: textSecondary }}>
                      Artikel & Berita
                    </span>
                    <div className="h-px flex-1" style={{ backgroundColor: borderColor }} />
                  </div>
                )}
                <StoriesComponent stories={filteredContent} onDeleteClick={handleDeleteClick} {...themeProps} />
              </div>
            )}

            {/* Instagram Posts */}
            {showPosts && filteredPosts.length > 0 && (
              <div>
                {activeKategori === "Semua" && (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1" style={{ backgroundColor: borderColor }} />
                    <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: "#E1306C" }}>
                      <FaInstagram /> Instagram Posts
                    </span>
                    <div className="h-px flex-1" style={{ backgroundColor: borderColor }} />
                  </div>
                )}
                <InstagramComponent posts={filteredPosts} onDeleteClick={handleDeleteClick} {...themeProps} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Delete Modal ── */}
      {showDeleteModal && selectedStory && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDeleteCancel} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden z-10"
            style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
          >
            <div className="px-6 py-4" style={{ background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" }}>
              <div className="flex items-center gap-3">
                <FaTimesCircle className="h-6 w-6 text-white" />
                <h3 className="text-lg font-bold font-serif text-white">Hapus Stories</h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#fef2f2" }}>
                  <FaTrash className="h-6 w-6 text-red-600" />
                </div>
                <p className="text-sm leading-relaxed" style={{ color: textPrimary }}>
                  Apakah Anda yakin ingin menghapus stories{" "}
                  <strong>"{selectedStory.judul || "Instagram Post #" + selectedStory.id}"</strong>{" "}
                  dari Rumoh Pangan Aceh? Tindakan ini tidak dapat dibatalkan.
                </p>
              </div>
            </div>
            <div
              className="px-6 py-4 border-t flex gap-3 justify-end"
              style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", borderColor }}
            >
              <button type="button" onClick={handleDeleteCancel}
                className="px-5 py-2.5 border rounded-xl text-sm font-medium transition-all duration-300"
                style={{ borderColor, color: textPrimary, backgroundColor: cardBg }}
              >
                Batal
              </button>
              <button type="button" onClick={() => handleDelete(selectedStory.id)}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300"
                style={{ backgroundColor: "#dc2626" }}
              >
                Hapus Stories
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ListStories;